/**
 * Team & Access — the compliance officer's user and app-access manager.
 *
 * Three tabs:
 *   1. Team       — current org members, per-app grants, search, invite actions
 *   2. Pending    — outstanding external invites + their expiry
 *   3. Audit      — append-only history (last 200 actions)
 *
 * Visual contract: calm PSI theme. Slate palette, thin borders, mono for
 * technical metadata, no emoji, no gradients, no AI personification copy.
 * Matches Knowledge Profile / EditChip.
 *
 * Routes hit: /api/admin/team/*  (see server/routes/adminTeam.js)
 *
 * SOC 2:
 *   - Org-scoped server-side enforced
 *   - HIPAA acknowledgment dialog before first grant per session
 *   - Every action you take here writes to psi_access_audit on the server
 */

import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import TeamList from './TeamList';
import PendingInvites from './PendingInvites';
import AuditLog from './AuditLog';
import InviteInternalDrawer from './InviteInternalDrawer';
import InviteExternalDrawer from './InviteExternalDrawer';
import AccessEditorDrawer from './AccessEditorDrawer';

const API = process.env.REACT_APP_API_URL || '';

const TABS = [
  { key: 'team',    label: 'Team' },
  { key: 'pending', label: 'Pending invites' },
  { key: 'audit',   label: 'Audit log' },
];

export default function TeamAccess() {
  const navigate = useNavigate();
  const [tab, setTab] = useState('team');
  const [data, setData] = useState(null);
  const [apps, setApps] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openInternal, setOpenInternal] = useState(false);
  const [openExternal, setOpenExternal] = useState(false);
  const [editorUser, setEditorUser] = useState(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const [teamRes, appsRes] = await Promise.all([
        fetch(`${API}/api/admin/team`,      { credentials: 'include' }),
        fetch(`${API}/api/admin/team/apps`, { credentials: 'include' }),
      ]);
      if (teamRes.status === 403 || appsRes.status === 403) {
        setError('You do not have permission to manage Team & Access.');
        return;
      }
      const teamJson = await teamRes.json();
      const appsJson = await appsRes.json();
      if (!teamJson.success || !appsJson.success) {
        setError(teamJson.message || appsJson.message || 'Could not load.');
        return;
      }
      setData(teamJson);
      setApps(appsJson.apps || []);
    } catch {
      setError('Connection error. Please refresh.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const userCount   = data?.users?.length || 0;
  const pendingCount = data?.pending_invites?.filter(i => i.status === 'pending').length || 0;

  return (
    <div style={{ minHeight: '100vh', background: '#f8fafc', color: '#0f172a' }}>
      {/* Header */}
      <header
        style={{
          background: '#ffffff', borderBottom: '1px solid #e2e8f0',
          padding: '24px 32px',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', maxWidth: 1280, margin: '0 auto', gap: 24 }}>
          <div>
            <button
              type="button"
              onClick={() => navigate('/app')}
              style={{ background: 'transparent', border: 0, padding: 0, fontSize: 12, color: '#475569', cursor: 'pointer', fontFamily: 'ui-monospace, monospace', letterSpacing: '0.06em', textTransform: 'uppercase' }}
            >
              ‹ Back to PSI
            </button>
            <h1 style={{ margin: '8px 0 4px 0', fontSize: 24, fontWeight: 600, letterSpacing: '-0.01em' }}>Team & Access</h1>
            <div style={{ fontSize: 13, color: '#64748b' }}>
              Add or remove teammates and observers. Grant access to specific applications, permanent or time-bound.
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              type="button"
              onClick={() => setOpenInternal(true)}
              style={{ background: '#1e293b', color: '#ffffff', border: '1px solid #1e293b', padding: '8px 14px', fontSize: 13, cursor: 'pointer', borderRadius: 8 }}
            >
              Invite teammate
            </button>
            <button
              type="button"
              onClick={() => setOpenExternal(true)}
              style={{ background: '#ffffff', color: '#0f172a', border: '1px solid #cbd5e1', padding: '8px 14px', fontSize: 13, cursor: 'pointer', borderRadius: 8 }}
            >
              Invite observer / executive
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div style={{ maxWidth: 1280, margin: '20px auto 0', display: 'flex', gap: 0, borderBottom: '1px solid #e2e8f0', marginBottom: -25 }}>
          {TABS.map(t => {
            const active = tab === t.key;
            const badge = t.key === 'team' ? userCount : (t.key === 'pending' ? pendingCount : null);
            return (
              <button
                key={t.key}
                type="button"
                onClick={() => setTab(t.key)}
                style={{
                  background: 'transparent', border: 0, padding: '10px 16px',
                  fontSize: 13, fontWeight: active ? 600 : 500,
                  color: active ? '#0f172a' : '#64748b',
                  cursor: 'pointer',
                  borderBottom: active ? '2px solid #0f172a' : '2px solid transparent',
                  marginBottom: -1,
                }}
              >
                {t.label}
                {badge != null && (
                  <span style={{
                    marginLeft: 8, fontSize: 11, padding: '1px 7px',
                    background: '#f1f5f9', color: '#475569',
                    border: '1px solid #e2e8f0', borderRadius: 999,
                    fontFamily: 'ui-monospace, monospace',
                  }}>
                    {badge}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </header>

      {/* Body */}
      <main style={{ maxWidth: 1280, margin: '0 auto', padding: '32px' }}>
        {loading && <div style={{ color: '#94a3b8', fontSize: 13 }}>Loading…</div>}
        {error && (
          <div style={{
            background: '#fff5f5', border: '1px solid #fecaca', color: '#b91c1c',
            padding: '12px 16px', borderRadius: 8, fontSize: 13,
          }}>
            {error}
          </div>
        )}
        {!loading && !error && data && (
          <>
            {tab === 'team' && (
              <TeamList
                users={data.users}
                apps={apps || []}
                onEditUser={(u) => setEditorUser(u)}
              />
            )}
            {tab === 'pending' && (
              <PendingInvites invites={data.pending_invites || []} />
            )}
            {tab === 'audit' && (
              <AuditLog />
            )}
          </>
        )}
      </main>

      {/* Drawers */}
      {openInternal && (
        <InviteInternalDrawer
          apps={apps || []}
          onClose={() => setOpenInternal(false)}
          onSaved={() => { setOpenInternal(false); load(); }}
        />
      )}
      {openExternal && (
        <InviteExternalDrawer
          apps={apps || []}
          onClose={() => setOpenExternal(false)}
          onSaved={() => { setOpenExternal(false); load(); }}
        />
      )}
      {editorUser && (
        <AccessEditorDrawer
          user={editorUser}
          apps={apps || []}
          onClose={() => setEditorUser(null)}
          onSaved={() => { setEditorUser(null); load(); }}
        />
      )}
    </div>
  );
}
